import api from '../../../../api/client'
import { toJobOption } from '../../common/pmOptionUtils'

function normalizeItems(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  return []
}

export async function listTimesheets(params) {
  const { data } = await api.get('/timesheets/', { params })
  return data
}

export async function listTimesheetApprovalUsers(params = {}) {
  const { data } = await api.get('/timesheets/approval-options/users', { params })
  return Array.isArray(data) ? data : []
}

export async function listScopedUserOptions(params = {}) {
  const { data } = await api.get('/options/users', {
    params: {
      page: 1,
      page_size: 200,
      ...params,
    },
  })
  return normalizeItems(data)
}

export async function getTimesheetDetail(timesheetId) {
  const { data } = await api.get(`/timesheets/${timesheetId}`)
  return data
}

export async function createTimesheetFromJob(jobId, payload) {
  const { data } = await api.post(`/timesheets/from-job/${jobId}`, payload)
  return data
}

export async function createTimesheet(payload) {
  const { data } = await api.post('/timesheets/', payload)
  return data
}

export async function updateTimesheet(timesheetId, payload) {
  const { data } = await api.put(`/timesheets/${timesheetId}`, payload)
  return data
}

export async function archiveTimesheet(timesheetId) {
  const { data } = await api.post(`/timesheets/${timesheetId}/archive`)
  return data
}

export async function approveTimesheet(timesheetId) {
  const { data } = await api.patch(`/timesheets/${timesheetId}/approve`)
  return data
}

export async function rejectTimesheet(timesheetId) {
  const { data } = await api.patch(`/timesheets/${timesheetId}/reject`)
  return data
}

export async function listTimesheetJobOptions(params = {}) {
  const { data } = await api.get('/jobs/', {
    params: {
      page: 1,
      page_size: 100,
      includeTotal: true,
      archive: false,
      fromDate: '1900-01-01',
      toDate: '9999-12-31',
      ...params,
    },
  })
  return normalizeItems(data)
}

/**
 * Paginated, searchable job option fetcher for AppCombobox lazy loading.
 * Mirrors fetchTaskOptionsPage / fetchJobOptionsPage in jobApi.js.
 * Excludes COMPLETED and CLOSED jobs so they cannot be logged against.
 * Returns { items: [{ value, label }], hasMore: boolean }
 */
export async function fetchTimesheetJobOptionsPage(search, page, pageSize = 20, assignedToId = null) {
  const params = {
    page,
    page_size: pageSize,
    search: search || '',
    includeTotal: true,
    archive: false,
    fromDate: '1900-01-01',
    toDate: '9999-12-31',
  }
  if (assignedToId) params.assignedTo = assignedToId

  const { data } = await api.get('/jobs/', { params })
  const raw = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []

  // Only surface active jobs — completed/closed cannot receive new timesheet entries
  const active = raw.filter(
    (job) => job.status !== 'COMPLETED' && job.status !== 'CLOSED',
  )
  const items = active.map(toJobOption).filter(Boolean)

  const total = Number(data?.total ?? 0)
  const hasMore = total > 0 ? page * pageSize < total : raw.length === pageSize
  return { items, hasMore }
}


export async function getJobById(jobId) {
  if (!jobId) return null
  const { data } = await api.get(`/jobs/${jobId}`)
  return data
}

export async function getJobTimesheetCount(jobId) {
  if (!jobId) return { count: 0 }
  const { data } = await api.get(`/jobs/${jobId}/timesheet-count`)
  return data
}

export async function bulkApproveTimesheets(payload) {
  const { data } = await api.patch('/timesheets/bulk-approve', payload)
  return data
}

export async function bulkRejectTimesheets(payload) {
  const { data } = await api.patch('/timesheets/bulk-reject', payload)
  return data
}


